import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { APP_VERSION } from '@/lib/appVersion';
import { forceUpdate } from '@/lib/forceUpdate';
import { useAppVersionGate } from '@/lib/useAppVersionGate';

/**
 * Gate global de versão do admin: overlay bloqueante quando o build local
 * fica abaixo do `minBuild` publicado (kill switch); fora disso, não
 * renderiza nada.
 *
 * Origem: sem issue — commit 17dc7b267, 2026-06-19 (version gate com force
 * update + versão embaixo do menu).
 *
 * Por quê: o admin é PWA e fica aberto em abas por dias; sem gate, um
 * operador seguia num bundle velho chamando callables cujo contrato já mudou.
 * O aviso não-bloqueante de "há nova versão" vive no rodapé do menu
 * (`NavVersionFooter`), espelhando o `UpdateLink` da sidebar do app.
 *
 * Contrato: lê o estado de `useAppVersionGate()`; só `FORCE_UPDATE` desenha
 * algo. O botão chama `forceUpdate()` (limpa caches e service workers e
 * recarrega). Sem escrita, sem telemetria.
 *
 * Usado por: `main.tsx` (montado uma vez, dentro do `ErrorBoundary` global e
 * fora do router — cobre `/login` também). Só web `apps/admin`. Teste:
 * `AppVersionGate.test.tsx`.
 *
 * Arquitetura: a fonte da verdade da versão mínima é o Firestore
 * (`/appConfig/admin`, doc próprio do admin, não `/appConfig/web`). O
 * `release.yml` (step "Publish appConfig/admin") publica
 * `minBuild = latestBuild` a cada deploy do admin, então qualquer build
 * defasado cai direto em `FORCE_UPDATE` — o `UPDATE_AVAILABLE` é raro e
 * transitório e fica a cargo do rodapé.
 *
 * Dicas: o overlay é um `<div>` fixed (sem ESC nem clique no fundo) em vez do
 * `<Modal>` do Cloudscape de propósito — o super-admin precisa atualizar antes
 * de seguir, sem escape. Falha de leitura do doc cai em `OK` por desenho
 * (`useAppVersionGate`): o gate não pode trancar o admin por bug nosso.
 *
 * Issues: #655 (kill switch editável na tela `/settings/app-versions`).
 */
export function AppVersionGate(): JSX.Element | null {
  const state = useAppVersionGate();

  if (state.kind !== 'FORCE_UPDATE') return null;
  return <ForceUpdateOverlay latestVersion={state.latestVersion} />;
}

/** Overlay fullscreen do `FORCE_UPDATE`: mensagem, versões e "Atualizar agora". */
function ForceUpdateOverlay({ latestVersion }: { latestVersion: string }): JSX.Element {
  const { t } = useTranslation();
  const [updating, setUpdating] = useState(false);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="force-update-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        background: 'rgba(0, 7, 22, 0.6)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 480 }}>
        <Container header={<Header variant="h2">{t('app.versionGate.forceTitle')}</Header>}>
          <SpaceBetween size="m">
            <Box variant="p" id="force-update-title">
              {t('app.versionGate.forceBody')}
            </Box>
            <Box variant="small" color="text-status-inactive">
              {t('app.versionGate.versionLine', {
                current: APP_VERSION,
                latest: latestVersion,
              })}
            </Box>
            <Button
              variant="primary"
              fullWidth
              loading={updating}
              onClick={(): void => {
                setUpdating(true);
                void forceUpdate();
              }}
            >
              {t('app.versionGate.updateNow')}
            </Button>
          </SpaceBetween>
        </Container>
      </div>
    </div>
  );
}
